import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shirt, Footprints, Watch, ShoppingBag, ArrowRight } from "lucide-react";
import AnnonceCard from "@/components/AnnonceCard";
import { fetchAnnonces } from "@/lib/api";

const MODE_CATEGORIES = ["Mode", "Montres & Bijoux", "Accessoires & Bagagerie"];

const COLUMNS = [
  { title: "Vetements", icon: Shirt, category: "Mode", items: ["Femme", "Maternite", "Homme", "Enfant"] },
  { title: "Chaussures", icon: Footprints, category: "Mode", items: ["Femme", "Homme", "Enfant"], queryHint: "chaussures" },
  { title: "Montres & Bijoux", icon: Watch, category: "Montres & Bijoux", items: ["Femme", "Homme", "Enfant", "Mixte"] },
  { title: "Accessoires & Bagagerie", icon: ShoppingBag, category: "Accessoires & Bagagerie", items: ["Femme", "Homme", "Enfant"] },
];

const buildHref = (cat, sub, hint) => {
  const p = new URLSearchParams();
  p.set("category", cat);
  if (sub) p.set("subcategory", sub);
  if (hint) p.set("q", hint);
  return `/annonces?${p.toString()}`;
};

export default function ModeUniverseSection() {
  const [tendances, setTendances] = useState([]);

  useEffect(() => {
    // Fetch 8 most recent items across the 3 Mode universes
    Promise.all(
      MODE_CATEGORIES.map((c) =>
        fetchAnnonces({ category: c, page_size: 4 }).then((r) => r.items || []).catch(() => [])
      )
    ).then((lists) => {
      // Round-robin merge so we have a mix of all 3 universes
      const merged = [];
      const max = Math.max(...lists.map((l) => l.length));
      for (let i = 0; i < max; i++) {
        for (const l of lists) {
          if (l[i]) merged.push(l[i]);
        }
      }
      setTendances(merged.slice(0, 8));
    });
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-6 mt-16" data-testid="mode-universe">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="text-[12px] uppercase tracking-widest text-orange-600 font-bold">Univers</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">
            Mode, montres & accessoires
          </h2>
          <p className="text-[var(--ink-soft)] mt-1">
            Toutes les pieces pour s'habiller, par profil — avec option de livraison Yango.
          </p>
        </div>
        <Link to="/annonces?category=Mode" className="hidden md:inline-flex items-center gap-1 text-orange-600 font-semibold" data-testid="mode-universe-see-all">
          Voir tout <ArrowRight size={14} />
        </Link>
      </div>

      {/* Visual product grid */}
      {tendances.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 mb-8" data-testid="mode-tendances-grid">
          {tendances.map((a, i) => (
            <div key={a.id} className="rise" style={{ animationDelay: `${i * 0.04}s` }}>
              <AnnonceCard a={a} />
            </div>
          ))}
        </div>
      )}

      {/* Navigation columns */}
      <div className="glass-card p-6 md:p-8">
        <div className="text-[12px] uppercase tracking-widest text-orange-600 font-bold mb-4">Parcourir par profil</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-7">
          {COLUMNS.map((col) => {
            const Icon = col.icon;
            return (
              <div key={col.title} data-testid={`mode-col-${col.title}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <h3 className="font-bold text-[15px]">{col.title}</h3>
                </div>
                <ul className="space-y-1.5 text-sm">
                  {col.items.map((sub) => (
                    <li key={sub}>
                      <Link
                        to={buildHref(col.category, sub, col.queryHint)}
                        className="footer-link hover:text-orange-600 inline-flex items-center gap-1"
                        data-testid={`mode-link-${col.title}-${sub}`}
                      >
                        {sub}
                      </Link>
                    </li>
                  ))}
                  <li className="pt-1">
                    <Link
                      to={buildHref(col.category, "", col.queryHint)}
                      className="text-orange-600 font-semibold text-xs"
                      data-testid={`mode-all-${col.title}`}
                    >
                      Tous &rarr;
                    </Link>
                  </li>
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
